import styled from 'styled-components';
import { InputSize } from './types';

const ICON_WIDTH = 16;
const ICON_HEIGHT = 40;
const ICON_HEIGHT_SMALL = 32;
const ICON_MIN_WIDTH = 24;

const PADDING = 7;
const PADDING_SMALL = 7;

const BUTTON_FONT_SIZE = 15;
const BUTTON_FONT_SIZE_SMALL = 13;

const STATUS_ICON_WIDTH = 32;

export const StyledWrapper = styled.div({
  position: 'relative',
});

export const StyledButtons = styled.div<{
  $isLeft?: boolean;
  $isRight?: boolean;
  $size: InputSize;
}>(({
  $isLeft, $isRight, $size, theme: { colors, input },
}) => ({
  position: 'absolute',
  top: 0,
  left: $isLeft ? 0 : 'unset',
  right: $isRight ? 0 : 'unset',
  whiteSpace: 'nowrap',
  color: colors.NEUTRAL_INACTIVE,
  display: 'flex',
  alignItems: 'center',
  height: input.SIZES[$size],
}));

export const StyledInputButton = styled.button<{
  disabled?: boolean;
  $small?: boolean;
}>(
  ({
    disabled,
    $small,

    theme: { colors, input },
  }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'bottom',
    backgroundColor: 'transparent',
    padding: 0,
    border: 'none',
    height: $small ? ICON_HEIGHT_SMALL : ICON_HEIGHT,
    outline: 'none',
    fontSize: $small ? BUTTON_FONT_SIZE_SMALL : BUTTON_FONT_SIZE,
    cursor: disabled ? 'default' : 'pointer',
    color: disabled
      ? colors[input.ICON_COLOR_DISABLED]
      : colors[input.ICON_COLOR],
    transition: 'color 0.15s',
    marginLeft: 4,

    '&:hover, &:focus': disabled
      ? undefined
      : {
        color: colors[input.ICON_COLOR_HOVER],
      },

    '&:active': disabled
      ? undefined
      : {
        color: colors[input.ICON_COLOR_ACTIVE],
      },

    '&:first-child': {
      marginLeft: 0,
    },
  }),
);

export const StyledInput = styled.input<{
  disabled?: boolean;
  $hasError?: boolean;
  $hasWarning?: boolean;
  $size: InputSize;
  $isOnlyBorderBottom?: boolean;
  $leftButtonsLength?: number;
  $rightButtonsLength?: number;
}>(
  ({
    disabled,
    $hasError,
    $hasWarning,
    $size,
    $isOnlyBorderBottom,
    $leftButtonsLength = 0,
    $rightButtonsLength = 0,

    theme: {
      colors, fonts, indents, input,
    },
  }) => ({
    ...input.FONT_SIZES[$size],
    boxSizing: 'border-box',
    width: '100%',
    borderRadius: $isOnlyBorderBottom ? 0 : input.BORDER_RADIUS,

    ...($isOnlyBorderBottom
      ? {
        borderTopWidth: 0,
        borderRightWidth: 0,
        borderLeftWidth: 0,
        borderBottomWidth: input.BORDER_WIDTH,
        borderStyle: 'solid',
        borderColor: disabled
          ? colors[input.BORDER_COLOR_DISABLED]
          : $hasError
            ? colors[input.BORDER_COLOR_ERROR]
            : $hasWarning
              ? colors[input.BORDER_COLOR_WARNING]
              : colors[input.BORDER_COLOR],
      }
      : {
        border: 0,
        outlineWidth: $hasError
          ? input.BORDER_WIDTH_HOVER
          : input.BORDER_WIDTH,
        outlineStyle: 'solid',
        outlineColor: disabled
          ? colors[input.BORDER_COLOR_DISABLED]
          : $hasError
            ? colors[input.BORDER_COLOR_ERROR]
            : $hasWarning
              ? colors[input.BORDER_COLOR_WARNING]
              : colors[input.BORDER_COLOR],
      }),

    paddingTop: $size === 'small' ? PADDING_SMALL : PADDING,
    paddingLeft:
      $leftButtonsLength > 0
        ? $leftButtonsLength * ICON_WIDTH + indents.INDENT_2
        : $size === 'small'
          ? PADDING_SMALL
          : PADDING,
    paddingBottom: $size === 'small' ? PADDING_SMALL : PADDING,
    paddingRight:
      $rightButtonsLength > 0
        ? $rightButtonsLength * ICON_WIDTH + indents.INDENT_2
        : $size === 'small'
          ? PADDING_SMALL
          : PADDING,
    color: disabled
      ? colors[input.TEXT_COLOR_DISABLED]
      : colors[input.TEXT_COLOR],
    backgroundColor: $isOnlyBorderBottom
      ? 'transparent'
      : $hasError
        ? colors[input.BG_COLOR_ERROR]
        : $hasWarning
          ? colors[input.BG_COLOR_WARNING]
          : disabled
            ? colors[input.BG_COLOR_DISABLED]
            : colors[input.BG_COLOR],
    resize: 'vertical',
    minHeight: input.SIZES[$size],
    transition: 'all 0.15s',

    '&::placeholder': {
      color: colors[input.PLACEHOLDER_COLOR],
    },

    '&::-ms-clear': {
      display: 'none',
    },

    '&:hover': disabled || $hasError || $hasWarning
      ? undefined
      : {
        ...($isOnlyBorderBottom
          ? {
            borderColor: colors[input.BORDER_COLOR_HOVER],
            borderTopWidth: 0,
            borderRightWidth: 0,
            borderLeftWidth: 0,
            borderBottomWidth: 1,
          }
          : {
            outlineColor: colors[input.BORDER_COLOR_HOVER],
            outlineWidth: input.BORDER_WIDTH_HOVER,
          }),
      },

    '&:focus': disabled
      ? undefined
      : {
        ...($isOnlyBorderBottom
          ? {
            borderColor: colors[input.BORDER_COLOR_FOCUS],
            borderTopWidth: 0,
            borderRightWidth: 0,
            borderLeftWidth: 0,
            borderBottomWidth: 1,
            outlineWidth: 0,
          }
          : {
            backgroundColor: colors[input.BG_COLOR_FOCUS],
            outlineColor: colors[input.BORDER_COLOR_FOCUS],
            outlineWidth: input.BORDER_WIDTH_FOCUS,
          }),
      },

    '&:-webkit-autofill': {
      ...($size === 'small' ? fonts.INPUT_SMALL : fonts.INPUT),

      '&, &:hover': {
        boxShadow: `0 0 0px 1000px ${colors[input.BG_COLOR]} inset`,
      },

      '&:focus': {
        boxShadow: `0 0 0px 1000px ${colors[input.BG_COLOR_FOCUS]} inset`,
      },
    },
  }),
);

export const StatusIconWrapper = styled.div<{ $isCenter: boolean }>(({
  $isCenter,
}) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: STATUS_ICON_WIDTH,
  width: '100%',
  justifyContent: $isCenter ? 'center' : undefined,
}));

export const ButtonWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  minWidth: ICON_MIN_WIDTH,
  height: '100%',
  justifyContent: 'center',
});
